import React, { createContext, useContext, useReducer, useEffect, useState, useRef } from 'react';
import { validateQuantity } from '../utils/validation';
import { logError } from '../utils/errorHandler';
import { useInventory } from './InventoryContext';
import { useAuth } from './AuthContext';
import { db } from '../firebase/config';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const CartContext = createContext();

// Constants
const MAX_QUANTITY_PER_ITEM = 99;
const MAX_CART_ITEMS = 50;
const MAX_HISTORY_LENGTH = 10;
const BULK_ORDER_THRESHOLD = MAX_QUANTITY_PER_ITEM;

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItem = state.items.find(item => item.id === action.payload.id);
      
      // Check max cart items limit
      if (!existingItem && state.items.length >= MAX_CART_ITEMS) {
        throw new Error(`Cannot add more than ${MAX_CART_ITEMS} different items to cart`);
      }
      
      let newItems;
      if (existingItem) {
        // Check quantity limit
        if (existingItem.quantity >= MAX_QUANTITY_PER_ITEM) {
          throw new Error(`Maximum quantity of ${MAX_QUANTITY_PER_ITEM} reached for this item`);
        }
        
        newItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: Math.min(item.quantity + 1, MAX_QUANTITY_PER_ITEM) }
            : item
        );
      } else {
        newItems = [...state.items, { ...action.payload, quantity: 1 }];
      }
      
      return {
        ...state,
        items: newItems,
        history: [...state.history, state.items].slice(-MAX_HISTORY_LENGTH)
      };
    }

    case 'REMOVE_FROM_CART': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      return {
        ...state,
        items: newItems,
        history: [...state.history, state.items].slice(-MAX_HISTORY_LENGTH)
      };
    }

    case 'UPDATE_QUANTITY': {
      // Validate quantity
      const validation = validateQuantity(action.payload.quantity, MAX_QUANTITY_PER_ITEM);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }
      
      const newItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: validation.value }
          : item
      );
      
      return {
        ...state,
        items: newItems,
        history: [...state.history, state.items].slice(-MAX_HISTORY_LENGTH)
      };
    }

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        history: [...state.history, state.items].slice(-MAX_HISTORY_LENGTH)
      };

    case 'UNDO':
      if (state.history.length === 0) {
        return state;
      }
      const previousState = state.history[state.history.length - 1];
      return {
        ...state,
        items: previousState,
        history: state.history.slice(0, -1)
      };

    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload,
        history: []
      };

    default:
      return state;
  }
};

const initialState = {
  items: [],
  history: []
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getStock } = useInventory();
  const { currentUser } = useAuth();
  
  // Ref to track if we've synced per user and if we're currently loading
  const syncedUsersRef = useRef(new Set());
  const loadingRef = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Load cart - from Firestore if signed in, localStorage if not
  useEffect(() => {
    if (loadingRef.current) return; // Prevent concurrent loads
    
    if (!currentUser) {
      // User not signed in - load from localStorage
      console.log('User not signed in, loading from localStorage');
      try {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          if (Array.isArray(parsedCart)) {
            dispatch({ type: 'LOAD_CART', payload: parsedCart });
          }
        }
      } catch (error) {
        logError(error, { action: 'loadCart' });
        localStorage.removeItem('cart');
      }
      return;
    }

    // User is signed in - load from Firestore
    if (syncedUsersRef.current.has(currentUser.uid)) {
      return;
    }

    loadingRef.current = true;

    const loadFromFirestore = async () => {
      try {
        setLoading(true);
        const cartDocRef = doc(db, 'users', currentUser.uid, 'cart', 'items');
        const cartDoc = await getDoc(cartDocRef);
        
        if (cartDoc.exists()) {
          const firestoreCart = cartDoc.data().items || [];
          dispatch({ type: 'LOAD_CART', payload: firestoreCart });
        } else {
          // If no cart in Firestore, check localStorage for sync
          const localCart = localStorage.getItem('cart');
          if (localCart) {
            try {
              const parsedCart = JSON.parse(localCart);
              if (Array.isArray(parsedCart) && parsedCart.length > 0) {
                await setDoc(cartDocRef, { items: parsedCart, timestamp: new Date() });
                dispatch({ type: 'LOAD_CART', payload: parsedCart });
              }
            } catch (parseError) {
              logError(parseError, { action: 'parseLocalCart' });
            }
          }
        }
        syncedUsersRef.current.add(currentUser.uid);
      } catch (error) {
        console.error('Error loading cart from Firestore:', error.message);
        logError(error, { action: 'loadCartFromFirestore', userId: currentUser?.uid });
        syncedUsersRef.current.add(currentUser.uid);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadFromFirestore();
  }, [currentUser]);

  // Save cart - to Firestore if signed in, localStorage if not (with debounce)
  useEffect(() => {
    // Don't save while loading
    if (loadingRef.current) return;

    // Don't save if cart is empty
    if (state.items.length === 0) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout to debounce saves
    saveTimeoutRef.current = setTimeout(() => {
      const saveCart = async () => {
        try {
          if (currentUser) {
            const cartDocRef = doc(db, 'users', currentUser.uid, 'cart', 'items');
            await setDoc(cartDocRef, { items: state.items, timestamp: new Date() });
            localStorage.removeItem('cart');
          } else {
            localStorage.setItem('cart', JSON.stringify(state.items));
          }
        } catch (error) {
          console.error('Error saving cart:', error.message);
          logError(error, { action: 'saveCart', userId: currentUser?.uid });
        }
      };

      saveCart();
    }, 500); // Debounce by 500ms

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state.items, currentUser]);

  const addToCart = (product, quantity = 1) => {
    try {
      setError(null);
      setLoading(true);

      // Validate product
      if (!product || !product.id || !product.price || !product.name) {
        throw new Error('Invalid product data');
      }

      const availableStock = getStock(product.id);
      const currentQuantity = state.items.find(item => item.id === product.id)?.quantity || 0;

      if (availableStock <= 0) {
        throw new Error('This item is currently out of stock');
      }

      if (currentQuantity >= availableStock) {
        throw new Error(`Only ${availableStock} item(s) available in stock`);
      }

      // Add quantity to payload
      const payload = { ...product, quantity };
      dispatch({ type: 'ADD_TO_CART', payload });

      return { success: true, message: 'Item added to cart' };
    } catch (error) {
      logError(error, { action: 'addToCart', productId: product?.id });
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = (productId) => {
    try {
      setError(null);
      dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
      return { success: true, message: 'Item removed from cart' };
    } catch (error) {
      logError(error, { action: 'removeFromCart', productId });
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const updateQuantity = (productId, quantity) => {
    try {
      setError(null);
      
      if (quantity < 1) {
        return removeFromCart(productId);
      }
      
      const availableStock = getStock(productId);
      if (availableStock <= 0) {
        throw new Error('This item is currently out of stock');
      }

      if (quantity > availableStock) {
        throw new Error(`Only ${availableStock} item(s) available in stock`);
      }

      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
      return { success: true, message: 'Quantity updated' };
    } catch (error) {
      logError(error, { action: 'updateQuantity', productId, quantity });
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const clearCart = async () => {
    try {
      setError(null);
      dispatch({ type: 'CLEAR_CART' });
      
      // Clear from localStorage immediately
      localStorage.removeItem('cart');
      
      // Clear from Firestore if user is signed in
      if (currentUser) {
        const cartDocRef = doc(db, 'users', currentUser.uid, 'cart', 'items');
        await setDoc(cartDocRef, { items: [], timestamp: new Date() });
      }

      return { success: true, message: 'Cart cleared' };
    } catch (error) {
      logError(error, { action: 'clearCart' });
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const undo = () => {
    try {
      if (state.history.length === 0) {
        return { success: false, message: 'Nothing to undo' };
      }
      
      dispatch({ type: 'UNDO' });
      return { success: true, message: 'Action undone' };
    } catch (error) {
      logError(error, { action: 'undo' });
      return { success: false, message: error.message };
    }
  };

  const getCartTotal = () => {
    try {
      return state.items.reduce((total, item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        return total + (price * quantity);
      }, 0);
    } catch (error) {
      logError(error, { action: 'getCartTotal' });
      return 0;
    }
  };

  const getCartCount = () => {
    try {
      return state.items.reduce((count, item) => {
        const quantity = parseInt(item.quantity) || 0;
        return count + quantity;
      }, 0);
    } catch (error) {
      logError(error, { action: 'getCartCount' });
      return 0;
    }
  };

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  const getItemQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  return (
    <CartContext.Provider value={{
      cart: state.items,
      loading,
      error,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      undo,
      undoAvailable: state.history.length > 0,
      getCartTotal,
      getCartCount,
      isInCart,
      getItemQuantity,
      maxQuantity: MAX_QUANTITY_PER_ITEM,
      bulkOrderThreshold: BULK_ORDER_THRESHOLD
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
